import React, { useState } from 'react';
import useDidUpdateEffect from '@use-effect/did-update';

import Label from 'Elements/Label';

import {
    SearchWrapper,
    InputWrapper,
    SearchBox,
    SearchButtonInner,
    StyledSearchIcon,
    SearchButtonOuter,
    ButtonIcon
} from './Search.styled';

export interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
    ariaLabel?: string;
    name: string;
    defaultValue?: string;
    handleValueChange?(value: string, name: string): void;
    handleSubmit?(value: string, name: string): React.SyntheticEvent | void;
    variant?: 'default' | 'button';
    button?: {
        ariaLabel?: string;
    };
    label?: string;
}

const Search: React.FC<Props> = props => {
    const {
        id,
        name,
        defaultValue,
        handleValueChange,
        handleSubmit,
        variant = 'default',
        button,
        label,
        ...rest
    } = props;
    const [value, setValue] = useState(defaultValue ? defaultValue : '');

    useDidUpdateEffect(() => {
        if (handleValueChange) {
            handleValueChange(value, name);
        }
    }, [value]);

    useDidUpdateEffect(() => {
        if (value !== defaultValue) {
            setValue(defaultValue ? defaultValue : '');
        }
    }, [defaultValue]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;

        setValue(value);
    };

    const handleClick = () => {
        if (handleSubmit) {
            handleSubmit(value, name);
        }
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter' && handleSubmit) {
            handleSubmit(value, name);
        }
    };

    return (
        <SearchWrapper>
            {label && (
                <Label id={`${id}-label`} htmlFor={id}>
                    {label}
                </Label>
            )}
            <InputWrapper>
                <SearchBox
                    id={id}
                    name={name}
                    type="search"
                    value={value}
                    onChange={handleChange}
                    onKeyDown={handleKeyPress}
                    $variant={variant}
                    {...rest}
                />
                {variant === 'default' && (
                    <SearchButtonInner
                        onClick={handleClick}
                        aria-label={button?.ariaLabel ? button.ariaLabel : label}
                    >
                        <StyledSearchIcon />
                    </SearchButtonInner>
                )}
                {variant === 'button' && (
                    <SearchButtonOuter
                        onClick={handleClick}
                        aria-label={button?.ariaLabel ? button.ariaLabel : label}
                    >
                        <ButtonIcon />
                    </SearchButtonOuter>
                )}
            </InputWrapper>
        </SearchWrapper>
    );
};

export default Search;
